import { useAtomValue } from 'jotai';
import { merge } from 'lodash-es';
import * as yup from 'yup';
import { useUserIdField } from '../../../data-fields/CommonFields';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { percentFieldHighPrecision0to100 } from '../../../schemas/common-schema-defs';
import { FundWaterfallType } from '../../../schemas/ClientConfig.schema';
import { FundDataModel, fundFields } from '../../../schemas/Fund.schema';
import { IUpdateFundFields } from '../../../services/queries/MaggieFundQueries';
import {
  collectStrategyOptions,
  fundStrategiesAtom,
  getFundWaterfallType,
} from '../../../services/state/AppConfigStateJ';

const BaseDigestFieldPaths = [
  'vintage',
  'lpCommitmentSplit',
  'slpCommitmentSplit',
  'gpCommitmentPercentage',
  'commitments',
  'investedManagementFee',
  'legalName',
  'stepDownDate',
  'taxId',
  'managementFeeTerminationDate',
  'generalPartners',
  'initialManagementFee',
  'investmentPeriodEndDate',
  'lineOfCredit',
  'inceptionDate',
  'finalCloseDate',
  'strategy',
] as const;

function getDigestFieldPaths() {
  const isFormulaBased = getFundWaterfallType() === FundWaterfallType.FormulaBased;
  if (isFormulaBased) {
    return [...BaseDigestFieldPaths, 'preferredReturnHurdle'] as const;
  }
  return BaseDigestFieldPaths;
}

export function useFPDigestSchema(fund: FundDataModel) {
  const fields = fundFields();

  const partnersMeta = useUserIdField({
    key: 'generalPartners',
    multi: true,
    label: 'General Partners',
  });

  // Every strategy used on any fund, as the fund config picker offers, plus this fund's own in case
  // it carries one the loaded fund list does not (yet).
  const allFundStrategies = useAtomValue(fundStrategiesAtom);
  const strategyValues: ISimpleChoice<string>[] = collectStrategyOptions([
    { strategy: allFundStrategies },
    fund,
  ]).map((strategy) => ({ value: strategy, displayName: strategy }));

  const { generalPartners, strategy, lpCommitmentSplit, commitments, preferredReturnHurdle } = fields;

  return yup
    .object({
      ...fields,
      preferredReturnHurdle: preferredReturnHurdle
        .default(0)
        .transform((value) => (value == null ? 0 : value)),
      commitments: commitments.optional(),
      generalPartners: generalPartners.meta({
        ...merge({}, partnersMeta),
      }),
      gpCommitmentPercentage: percentFieldHighPrecision0to100()
        .label('GP Commitment %')
        .nullable()
        .default(null)
        .formMeta({
          disabled: true,
        }),
      lpCommitmentSplit: lpCommitmentSplit.optional(),
      strategy: strategy.meta({
        ...strategy.meta,
        rendererMeta: {
          allowCustomAdd: true,
          values: strategyValues,
        },
      }),
    })
    .pick(getDigestFieldPaths());
}
export type FPDigestSchema = Omit<
  yup.InferType<ReturnType<typeof useFPDigestSchema>>,
  'managementFeeTerminationDate'
> & {
  managementFeeTerminationDate?: string | null;
};

export function digestViewModelToFundDataModel(
  fundId: number,
  data: Partial<FPDigestSchema>
): IUpdateFundFields {
  const { gpCommitmentPercentage, ...fundDataModel } = data;

  return {
    ...fundDataModel,
    id: fundId,
  };
}

export function fundDataModelToDigestViewModel(data: FundDataModel): FPDigestSchema {
  return {
    ...data,
    preferredReturnHurdle: data.preferredReturnHurdle ?? 0,
    gpCommitmentPercentage:
      data.lpCommitmentSplit != null || data.slpCommitmentSplit != null
        ? 100 - (data.lpCommitmentSplit ?? 0) - (data.slpCommitmentSplit ?? 0)
        : null,
  };
}
